import { useState, useEffect, useRef, useCallback } from "react";
import { AuthContextProps } from "react-oidc-context";
import { getSessionStatus, replyToSession, KnownIssueMatch, isSessionComplete } from "../utils/backend-utils";
import config from "../config.json";

const DEBUG_MODE = config.development?.flg_debug ?? false;

// Session execution states
export type ExecutionPhase = 'editing' | 'starting' | 'polling' | 'awaiting_input' | 'submitting_reply' | 'completed' | 'error';

// Chat message type
export interface ChatMessage {
    id: string;
    text: string;
    isBot: boolean;
    timestamp: Date;
    signedAttachments?: string[]; // Presigned URLs for displaying attachments (from backend)
    localFiles?: File[]; // Local file objects for user messages (before backend response)
    planPlaceholder?: boolean; // Marks where the plan should be displayed in message history
    /** When set, the chat renders a special card instead of a normal message
     *  bubble. Used at session completion to surface the structured report
     *  via a CTA rather than dumping the report text inline, and during
     *  execution to surface deterministic known-issue diagnostics from
     *  the backend's regex match against known_issues.json. */
    messageType?: 'text' | 'report_cta' | 'known_issue' | 'transcript';
    /** Session id the CTA should link to (only meaningful when messageType
     *  === 'report_cta'). */
    reportSessionId?: string;
    /** True if the underlying session is a per-customer engagement
     *  template. Defaults to false on older payloads. */
    reportIsCustomerTemplate?: boolean;
    /** Short noun used in the card title and button label, e.g.
     *  "Installation" or "Tuning". Empty/undefined for generic session
     *  reports. */
    reportKind?: string;
    /** Deterministic known-issue regex matches for the preceding user
     *  reply. Only meaningful when messageType === 'known_issue'. */
    knownIssueMatches?: KnownIssueMatch[];
    /** Raw backend message timestamp for linking cost ledger entries. */
    sourceTimestamp?: string;
    /** Highlight when user jumps here from the cost popover. */
    highlight?: boolean;
}

interface UseSessionPollingOptions {
    sessionId: string | null;
    auth: AuthContextProps;
    onDebugToast?: (message: string) => void;
    initialMessages?: ChatMessage[];
}

export function useSessionPolling({ sessionId, auth, onDebugToast, initialMessages }: UseSessionPollingOptions) {
    const [executionPhase, setExecutionPhase] = useState<ExecutionPhase>('editing');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [sessionResponse, setSessionResponse] = useState<Record<string, any> | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>(initialMessages || []);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [totalSteps, setTotalSteps] = useState<number>(0);
    
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastAddedQuestionRef = useRef<string | null>(null);
    const lastAddedKnownIssueIdRef = useRef<string | null>(null);
    const hasStartedExecutionRef = useRef<boolean>(false);
    const maxStepReachedRef = useRef<number>(0);
    const hasInitializedMessagesRef = useRef<boolean>(false);

    // Initialize messages once from prop
    useEffect(() => {
        if (initialMessages && initialMessages.length > 0 && !hasInitializedMessagesRef.current) {
            setChatMessages(initialMessages);
            // Seed the known-issue dedup ref with the most recent restored card.
            // Otherwise the first poll's `current_known_issues_id` (which is the
            // same timestamp as the card already in history) would re-add a
            // duplicate card on page reload.
            const restoredKnownIssue = [...initialMessages].reverse().find(m => m.messageType === 'known_issue');
            if (restoredKnownIssue) {
                const tsMatch = restoredKnownIssue.id.match(/^restored-\d+-(.+)$/);
                if (tsMatch) {
                    lastAddedKnownIssueIdRef.current = tsMatch[1];
                }
            }
            hasInitializedMessagesRef.current = true;
        }
    }, [initialMessages]);

    // Show debug toast helper
    const showDebugToast = useCallback((message: string) => {
        if (DEBUG_MODE) {
            console.log('[DEBUG TOAST]', message);
            onDebugToast?.(message);
        }
    }, [onDebugToast]);

    // Polling function
    const pollSessionStatus = useCallback(async () => {
        if (!sessionId) return;
        
        try {
            const response = await getSessionStatus(sessionId, auth);
            console.log('[POLLING] Full response:', response);
            setSessionResponse(response);
            
            // Parse progress from debug_plan_progress (format: "0 / 3", "1 / 3", etc.)
            // This represents: completedSteps / totalPlanSteps
            // Where completedSteps indicates how many plan steps are done
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const debugProgress = (response as any).debug_plan_progress;
            if (debugProgress && typeof debugProgress === 'string') {
                const parts = debugProgress.split(' / ');
                if (parts.length === 2) {
                    const completedSteps = parseInt(parts[0], 10);
                    const backendTotalSteps = parseInt(parts[1], 10);
                    
                    if (!isNaN(completedSteps) && !isNaN(backendTotalSteps)) {
                        // Backend progress "0 / 3" means:
                        // - 0 plan steps completed
                        // - Working on first plan step
                        // - In UI: "Creating plan" ✓, "Step 1" → (active)
                        // - activeStepIndex = 1 (completedSteps + 1)
                        //
                        // Backend progress "1 / 3" means:
                        // - 1 plan step completed
                        // - Working on second plan step  
                        // - In UI: "Creating plan" ✓, "Step 1" ✓, "Step 2" → (active)
                        // - activeStepIndex = 2 (completedSteps + 1)
                        //
                        // Therefore: activeStepIndex = completedSteps + 1
                        const newStep = completedSteps + 1;
                        
                        // IMPORTANT: Only update if progress moves forward (monotonic progress)
                        // This prevents race conditions from causing backwards progress jumps
                        if (newStep > maxStepReachedRef.current) {
                            maxStepReachedRef.current = newStep;
                            setCurrentStep(newStep);
                        }
                        
                        // Always update total steps (in case plan was modified)
                        setTotalSteps(backendTotalSteps);
                    }
                }
            } else if (hasStartedExecutionRef.current && maxStepReachedRef.current === 0) {
                // If execution has started but we don't have progress yet,
                // default to step 1 (showing "Creating plan" as completed, first plan step active)
                maxStepReachedRef.current = 1;
                setCurrentStep(1);
            }
            
            // Show debug toast with status prefix
            const statusPrefix = `[${response.status}]`;
            showDebugToast(`${statusPrefix}\n${JSON.stringify(response, null, 2)}`);
            
            // Check status and handle accordingly
            if (response.status === 'CLARIFICATION_NEEDED') {
                // This shouldn't happen during execution polling, but handle it gracefully
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                setExecutionPhase('error');
                setErrorMessage('Unexpected clarification request during execution');
            } else if (response.status === 'DRAFTING') {
                // This shouldn't happen during execution polling, but handle it gracefully
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                setExecutionPhase('error');
                setErrorMessage('Session is still in drafting phase');
            } else if (response.status === 'NEEDS_INPUT') {
                // Stop polling
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }

                // If the user's last reply triggered a deterministic known-issue
                // match, drop the diagnostic card BEFORE the bot's reply so the
                // chat reads: user → known-issue card → bot bubble. Dedup on
                // the source message timestamp so subsequent polls don't
                // re-add the same card.
                const knownIssueId = response.current_known_issues_id;
                const knownIssues = response.current_known_issues;
                if (knownIssueId && knownIssues && knownIssues.length > 0
                    && knownIssueId !== lastAddedKnownIssueIdRef.current) {
                    lastAddedKnownIssueIdRef.current = knownIssueId;
                    setChatMessages(prev => [...prev, {
                        id: `known-issue-${knownIssueId}`,
                        text: '',
                        isBot: true,
                        timestamp: new Date(),
                        messageType: 'known_issue',
                        knownIssueMatches: knownIssues,
                    }]);
                }

                // Add the question to chat messages (prevent duplicates)
                if (response.current_question && response.current_question !== lastAddedQuestionRef.current) {
                    lastAddedQuestionRef.current = response.current_question;
                    setChatMessages(prev => [...prev, {
                        id: `bot-${Date.now()}`,
                        text: response.current_question!,
                        isBot: true,
                        timestamp: new Date(),
                    }]);
                }

                setExecutionPhase('awaiting_input');
            } else if (isSessionComplete(response.status)) {
                // Stop polling
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }

                // The backend now produces a structured installation report
                // rendered at /sessions/:id/report. Instead of dumping the
                // raw last message into the chat (which made the "report"
                // look like just another bot reply), we surface a CTA card
                // that links to the document view. We still append the raw
                // text as a fallback when the structured report isn't ready
                // yet — e.g. legacy sessions, or transient backend errors.
                if (response.report_available && sessionId) {
                    const isCustomer = response.report_is_customer_template === true;
                    const kind = (response.report_kind || '').trim();
                    setChatMessages(prev => {
                        // Idempotent: don't push duplicate CTAs on re-poll.
                        if (prev.some(m => m.messageType === 'report_cta')) return prev;
                        // Card text comes from the per-template `report_kind`
                        // when present (so a tuning session reads "Your
                        // Tuning documentation is ready"), falling back to
                        // generic wording for ad-hoc sessions.
                        const text = isCustomer && kind
                            ? `Your ${kind} documentation is ready.`
                            : isCustomer
                                ? 'Your documentation is ready.'
                                : 'Your session report is ready.';
                        return [...prev, {
                            id: `bot-report-cta-${Date.now()}`,
                            text,
                            isBot: true,
                            timestamp: new Date(),
                            messageType: 'report_cta',
                            reportSessionId: sessionId,
                            reportIsCustomerTemplate: isCustomer,
                            reportKind: kind || undefined,
                        }];
                    });
                } else {
                    const completionMessage = response.final_report || 'Session completed successfully.';
                    setChatMessages(prev => [...prev, {
                        id: `bot-complete-${Date.now()}`,
                        text: completionMessage,
                        isBot: true,
                        timestamp: new Date(),
                    }]);
                }

                setExecutionPhase('completed');
            }
            // If still RUNNING, continue polling (interval handles this)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
            setExecutionPhase('error');
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }
    }, [sessionId, auth, showDebugToast]);

    // Start polling
    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        
        setExecutionPhase('polling');
        
        // Mark that execution has started
        hasStartedExecutionRef.current = true;
        
        // Set initial progress to show "Creating plan" as completed
        // This will be overridden by backend progress once available
        maxStepReachedRef.current = 1;
        setCurrentStep(1);
        
        // Initial poll
        pollSessionStatus();
        
        // Set up polling interval. A long-running plan step can execute for
        // many minutes, so polling every second burns through the API
        // Gateway usage-plan quota fast (one busy session can add up to
        // thousands of requests/day on its own). 2.5s is still responsive
        // enough for progress feedback while meaningfully cutting volume.
        pollingIntervalRef.current = setInterval(pollSessionStatus, 2500);
    }, [pollSessionStatus]);

    // Stop polling
    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    // Send reply to bot's question
    const sendReply = useCallback(async (answer: string, attachmentKeys?: string[], localFiles?: File[]) => {
        // Fallback text if user uploads file but leaves chat text blank
        let finalAnswer = answer.trim();
        if (!finalAnswer && attachmentKeys && attachmentKeys.length > 0) {
            finalAnswer = "Look at the image provided";
        }

        if (!sessionId || !finalAnswer) return;

        // Add user message to chat (with local files for immediate display)
        setChatMessages(prev => [...prev, {
            id: `user-${Date.now()}`,
            text: finalAnswer,
            isBot: false,
            timestamp: new Date(),
            localFiles: localFiles,
        }]);
        
        setExecutionPhase('submitting_reply');
        setErrorMessage(null);
        lastAddedQuestionRef.current = null; // Reset so next question can be added

        try {
            // Send reply to backend
            const replyResponse = await replyToSession(sessionId, finalAnswer, auth, attachmentKeys);
            console.log('[REPLY] Response from reply endpoint:', replyResponse);

            // Interrogation sessions return LISTENING/NEEDS_INPUT — not guided
            // execution. Starting the guided polling loop here would leave the UI
            // stuck in "polling" with a typing indicator and no plan progress.
            if (replyResponse.status === 'LISTENING' || replyResponse.status === 'NEEDS_INPUT') {
                const question = replyResponse.current_question;
                if (question && question !== lastAddedQuestionRef.current) {
                    lastAddedQuestionRef.current = question;
                    setChatMessages(prev => [...prev, {
                        id: `bot-${Date.now()}`,
                        text: question,
                        isBot: true,
                        timestamp: new Date(),
                    }]);
                }
                setExecutionPhase('awaiting_input');
            } else {
                // Guided execution: resume polling after a short delay
                setTimeout(() => {
                    startPolling();
                }, 1000);
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
            setExecutionPhase('error');
        }
    }, [sessionId, auth, startPolling]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    return {
        executionPhase,
        setExecutionPhase,
        sessionResponse,
        errorMessage,
        setErrorMessage,
        chatMessages,
        setChatMessages,
        currentStep,
        totalSteps,
        startPolling,
        stopPolling,
        sendReply,
    };
}
